import { Color } from '@ardoq/design-tokens';
import { MinimalIconProps } from '@ardoq/icons';
import { DefaultMenuItem, MenuItemDef } from 'ag-grid-enterprise';
import type {
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
} from 'ag-grid-enterprise';
import { AriaAttributes, ComponentType, ReactNode } from 'react';
import {
  GroupCellLabels,
  SelectionColumnLabels,
  TableEmptyStateLabels,
  TableLocaleTextLabels,
} from '../labels';

export type ProtectedColumnId = '_group';

export type RowStatusType = 'error' | 'warning';

export type Row<Data, RowId extends string = string> = {
  id: RowId;
  data: Data;
  status?: RowStatusType;

  /** Grouping: An array of ids. The last element must be the id of this row */
  path?: RowId[];
  /** Grouping: Allows the row to have children dropped into it */
  allowDrop?: boolean;
  onClick?: VoidFunction;
  /** Context menu items for this row. Used as fallback when column doesn't define its own. */
  getContextMenuItems?: (
    rowData: Data,
    selectedRows: Data[]
  ) => (SupportedDefaultMenuItem | CustomContextMenuItem<Data, RowId>)[];
};

/**
 * A full-width row that spans across all columns.
 * Full-width rows are useful for custom content like banners, AI suggestions, or summary rows.
 *
 * The `FullWidthData` generic is independent of the column `RowData`, so full-width rows
 * don't need to include dummy fields to satisfy column type requirements.
 */
export type FullWidthRow<
  FullWidthData = unknown,
  RowId extends string = string,
> = {
  id: RowId;
  data: FullWidthData;
  fullWidth: true;
};

/**
 * A ghost row is a minimal placeholder for selected IDs that are not in
 * the current rows. These give AG Grid row nodes so selection state is
 * preserved when the parent filters rows out. They are hidden via external
 * filter and carry no real data.
 */
export type GhostRow<RowId extends string = string> = {
  id: RowId;
  data: unknown;
  isGhost: true;
};

export const isGhostRow = (row: unknown): row is GhostRow =>
  typeof row === 'object' &&
  row !== null &&
  'isGhost' in row &&
  row.isGhost === true;

/**
 * Union of all possible row types that AG Grid may hold in a given table.
 *
 * AG Grid's internal row model treats all rows uniformly — it does not
 * distinguish between normal rows, full-width rows, or ghost rows at the
 * type level. Using this type as the AG Grid generic (instead of
 * `Row<RowData>` alone) ensures that callbacks receiving row data via the
 * AG Grid API honestly reflect that the data may be any of these types.
 */
export type AnyRow<
  RowData,
  FullWidthRowData = never,
  RowId extends string = string,
> = Row<RowData, RowId> | FullWidthRow<FullWidthRowData> | GhostRow<RowId>;

export const isFullWidthRow = <
  RowData,
  FullWidthRowData = never,
  RowId extends string = string,
>(
  row: AnyRow<RowData, FullWidthRowData, RowId>
): row is FullWidthRow<FullWidthRowData> => 'fullWidth' in row;

export type ColumnAlignment = 'left' | 'center' | 'right';

export type AlignedColumn = {
  align?: ColumnAlignment;
};

export type PastedData<RowData> = {
  [K in keyof RowData]?: string;
};

export type OnPasteRow<RowData> = { pasted: PastedData<RowData>; row: RowData };

export type ProcessDataFromClipboard<RowData> = (
  data: OnPasteRow<RowData>[]
) => OnPasteRow<RowData>[] | null;

export type CellValueChangePayload<RowData, RowId extends string = string> = {
  row: Row<RowData, RowId>;
  value: string;
};

export type ColumnHeaderIcon = MinimalIconProps & {
  tooltip: string;
};

export type TooltipConfig<RowData> = {
  headerTooltip?: string;
  cellTooltip?: (data: RowData) => string;
};

export type CustomContextMenuItem<
  RowData,
  RowId extends string = string,
> = Omit<MenuItemDef<Row<RowData, RowId>>, 'action' | 'icon'> & {
  onClick: (params: RowData) => void;
  color?: Color;
  /** Icon shown to the left of the menu item. */
  icon?: MinimalIconProps;
};

export type SupportedDefaultMenuItem = Extract<
  DefaultMenuItem,
  | 'autoSizeAll'
  | 'autoSizeThis'
  | 'copy'
  | 'copyWithHeaders'
  | 'rowGroup'
  | 'separator'
>;

export type BaseColumn<
  RowData,
  Key extends keyof RowData,
  RowId extends string = string,
> = AlignedColumn & {
  title: string;
  getContextMenuItems?: (
    rowData: RowData,
    selectedRows: RowData[]
  ) => (SupportedDefaultMenuItem | CustomContextMenuItem<RowData, RowId>)[];
  tooltip?: TooltipConfig<RowData>;

  /** Icon shown after the title. */
  headerIcon?: ColumnHeaderIcon;

  /**
   * Hides the kebab menu in the header cell and disables header context menu.
   */
  suppressHeaderContextMenu?: boolean;

  /**
   * Disables sorting for this column.
   */
  suppressSorting?: boolean;

  /**
   * Enables drag and drop grouping of the column.
   */
  enableRowGroup?: boolean;

  /**
   * Allows in-place editing of the cell value.
   */
  onCellValueChange?: (params: CellValueChangePayload<RowData, RowId>) => void;
  /**
   * Optional comparator function to sort the column.
   *
   * Takes precedence over the default lowercase / numeric comparator and
   * over the no-op installed by {@link SortableProps.externallySorted}, so
   * a column with its own `comparator` will still apply it even when the
   * table is marked as externally sorted.
   */
  comparator?: (a: RowData[Key] | null, b: RowData[Key] | null) => number;
  /**
   * Used to let AG grid know how to go from the rows data object to
   * a string.
   *
   * This is used to check when the cell needs re-rendering, as well as for
   * filtering and in-place editing of the cell value, among other things.
   */
  valueGetter?: (data: NonNullable<RowData[Key]>) => string;

  /**
   * Column width in pixels
   */
  width?: number;
  /**
   * Minimum column width in pixels
   */
  minWidth?: number;
  /**
   * Maximum column width in pixels
   */
  maxWidth?: number;
  /**
   * Maximum width applied after the user invokes "Autosize This Column" or
   * "Autosize All Columns" from the context menu. Unlike `maxWidth`, this does
   * NOT constrain manual drag-resize — the user can still widen the column
   * past this value by dragging the header border.
   *
   * If the cap is below the column's `minWidth`, the resulting width is
   * `minWidth`. When set to a non-positive or non-finite number, the column
   * uses the default uncapped autosize behavior.
   */
  maxAutoSizeWidth?: number;
  /**
   * Flex value for responsive column sizing
   */
  flex?: number;
  /**
   * Controls whether the column can be resized by dragging the header border.
   * Defaults to true if not specified.
   */
  resizable?: boolean;
  /**
   * Enables auto-height for this column, allowing the row to grow to fit the
   * cell content. Useful for cells with variable-height content.
   */
  autoHeight?: boolean;
};

export type CellEditorProps<RowData, Key extends keyof RowData> = {
  data: NonNullable<RowData[Key]>;
  value: string;
  onValueChange: (value: string) => void;
};

export type ObjectColumn<
  RowData,
  Key extends keyof RowData,
  RowId extends string = string,
> = BaseColumn<RowData, Key, RowId> & {
  id: Key;
  /**
   * A React component that is used to render the cell.
   *
   * This will only be re-rendered when the `valueGetter` returns a different
   * value.
   *
   * @param data The data at the relevant attribute for the row
   * @returns The React node to render in the cell
   */
  cellRenderer: (data: NonNullable<RowData[Key]>) => React.ReactNode;
  valueGetter: (data: NonNullable<RowData[Key]>) => string;
  cellEditor?: (props: CellEditorProps<RowData, Key>) => React.ReactNode;
};

/**
 * Column for attributes that are primitive in the RowData
 *
 * Primitive attributes can not have a cellRenderer, as they can't be
 * passed as props to a React component.
 */
export type PrimitiveColumn<
  RowData,
  Key extends keyof RowData,
  RowId extends string = string,
> = BaseColumn<RowData, Key, RowId> & { id: Key };

type RegularColumnAtKey<
  RowData,
  Key extends keyof RowData,
  RowId extends string = string,
> = RowData[Key] extends Record<string, unknown> | null
  ? ObjectColumn<RowData, Key, RowId>
  : PrimitiveColumn<RowData, Key, RowId>;

type ColumnAtKey<
  RowData,
  Key extends keyof RowData,
  RowId extends string = string,
> = Key extends ProtectedColumnId
  ? GroupingColumnAtKey<RowData, Key, RowId>
  : RegularColumnAtKey<RowData, Key, RowId>;

export type Column<RowData, RowId extends string = string> =
  // This is a TS trick that gives a union of the columns for each key in RowData.
  { [Key in keyof RowData]: ColumnAtKey<RowData, Key, RowId> }[keyof RowData];

export type RegularColumn<RowData, RowId extends string = string> = {
  [Key in keyof RowData]: Key extends ProtectedColumnId
    ? never
    : RegularColumnAtKey<RowData, Key, RowId>;
}[keyof RowData];

export type SingleSelectableProps<RowId extends string = string> = {
  mode: 'single';
  id: RowId | null;
  onChange: (id: RowId | null) => void;
};

export type MultiSelectableProps<
  RowId extends string = string,
  RowData = unknown,
> = {
  mode: 'multiple';
  ids: RowId[];
  onChange: (ids: RowId[]) => void;

  /**
   * When you are using a table with grouping and multi selection, you can control
   * how group selection works with this prop. It has two modes:
   *
   * `self`: Selecting a group only selects that group row, and not its children.
   *
   * `descendants`: Selecting a group also selects all of its descendant rows.
   *
   *  The default is `self`, which means that groups are not automatically selected
   *  when you select all child rows, and is the option we want to use when the table
   *  has groups that are entities on their own (e.g. folders in document archive).
   *
   *  The `descendants` option is useful when groups are just a way to visually group
   *  rows together (e.g. grouping by solution in the metamodel constraints table)
   */
  groupSelects?: 'self' | 'descendants';

  /**
   * Set to `true` to hide the select-all checkbox in the header. Use this when
   * a custom "select all" affordance lives outside the table (e.g. one that
   * selects across pagination, not just loaded rows).
   */
  hideHeaderCheckbox?: boolean;

  /**
   * Set to `true` to let clicking anywhere on a row toggle its selection, in
   * addition to the checkbox. Each click selects or deselects a single row
   * without needing modifier keys, mirroring the checkbox behaviour.
   */
  enableRowClickSelection?: boolean;

  /**
   * Accessible name for this row's selection checkbox, e.g. the component name.
   *
   * Without it AG Grid names every row checkbox with the same generic string,
   * so a screen reader user arrowing down the column cannot tell which row they
   * are about to select.
   */
  getRowLabel?: (row: RowData) => string;
};

export type SelectableProps<RowId extends string = string, RowData = unknown> =
  | SingleSelectableProps<RowId>
  | MultiSelectableProps<RowId, RowData>;

export type CellCoordinate<RowId extends string = string> = {
  rowId: RowId;
  columnId: string;
};

/**
 * Lets you control which cell is currently being edited, with one requirement:
 * You must always update the coordinate state when onSync is called because
 * this is what AG-Grid has already applied.
 */
export type EditableProps<RowId extends string = string> = {
  coordinate: CellCoordinate<RowId> | null;
  /**
   * Changes in AG-Grid's internal state are synced with this callback, and
   * cannot be cancelled. Always set state accordingly.
   */
  onSync: (coordinate: CellCoordinate<RowId> | null) => void;
};

export type SortDescriptor = {
  column: string;
  direction: 'asc' | 'desc';
};

export type SortableProps = {
  /** Use `null` to initialize the table without a selected sorting. */
  descriptor: SortDescriptor | null;
  onChange?: (descriptor: SortDescriptor | null) => void;
  /**
   * Set to `true` when `rowData` is already sorted according to `descriptor`
   * (e.g. server-side sort, or a custom client-side sort in the prop mapper).
   * AG Grid will keep the rows in the order they are passed in instead of
   * applying its built-in lowercase string / numeric comparator.
   *
   * This replaces the *default* per-column comparator with a no-op. A column
   * that defines its own {@link BaseColumn.comparator} still takes precedence
   * and will be applied even when `externallySorted` is true.
   */
  externallySorted?: boolean;
};

export type ReorderableProps<RowData> = {
  onReorder: (sourceRowIndex: number, targetRowIndex: number) => void;
  /**
   * Overrides the default drag text shown when dragging rows.
   * Overrides the one provided by DragAndDroppableProps if both are provided.
   *
   */
  rowDragText?: (rowsData: RowData[]) => string;
};

/**
 * Base props for internal drag and drop functionality.
 */
type InternalDragAndDroppableProps<RowData, RowId extends string = string> = {
  /**
   * Callback fired when a row is dropped onto another row (folder) in the grid.
   * @param rowId The ID of the row being dropped
   * @param parentRowId The ID of the target parent row, or null if dropped at root level
   */
  onDrop: (rowId: RowId, parentRowId: RowId | null) => void;
  /**
   * When true, dropping onto a collapsed folder targets that folder. By
   * default a collapsed folder is not a drop target (the drop goes to its
   * parent) until it has been expanded, e.g. by hovering it during the drag.
   */
  dropIntoClosedFolders?: boolean;
  /**
   * Overrides the default drag text shown when dragging rows.
   * Is overridden by ReorderableProps when both are provided.
   */
  rowDragText?: (rowsData: RowData[]) => string;
};

/**
 * Props for enabling external drop zones outside the grid.
 * External drop zones allow users to drag rows from the grid and drop them
 * onto other elements on the page.
 */
export type ExternalDropZonesProps<RowData, RowId extends string = string> = {
  /**
   * Array of DOM element IDs for external drop zones.
   * Elements will be looked up via document.getElementById at drag time.
   */
  dropZoneIds: string[];
  /**
   * Callback fired when rows are dropped onto an external drop zone.
   * @param dropZoneId The ID of the drop zone that was dropped onto
   * @param rows The rows that were dropped
   */
  onExternalDrop: (dropZoneId: string, rows: Row<RowData, RowId>[]) => void;
  /**
   * Optional callback fired when dragging enters a drop zone.
   * @param dropZoneId The ID of the drop zone being entered
   */
  onDragEnterDropZone?: (dropZoneId: string) => void;
  /**
   * Optional callback fired when dragging leaves a drop zone.
   * @param dropZoneId The ID of the drop zone being left
   */
  onDragLeaveDropZone?: (dropZoneId: string) => void;
  /**
   * Optional callback fired when row dragging starts.
   * Useful for showing visual feedback on drop zones.
   * @param rows The rows being dragged (multiple if selected rows are dragged)
   */
  onDragStart?: (rows: Row<RowData, RowId>[]) => void;
  /**
   * Optional callback fired when row dragging ends (either by dropping or cancelling).
   * Useful for hiding visual feedback on drop zones.
   */
  onDragEnd?: VoidFunction;
};

export type DragAndDroppableProps<RowData, RowId extends string = string> = {
  rowDragText?: (rowsData: RowData[]) => string;
  /**
   * When true, rows can be dragged by clicking and holding anywhere on the
   * row, not just on the drag handle. Note that this disables cell text
   * selection while enabled.
   */
  dragEntireRow?: boolean;
} & (
  | {
      internalDrop: InternalDragAndDroppableProps<RowData, RowId>;
      externalDrop?: ExternalDropZonesProps<RowData, RowId>;
    }
  | {
      internalDrop?: InternalDragAndDroppableProps<RowData, RowId>;
      externalDrop: ExternalDropZonesProps<RowData, RowId>;
    }
);

export type PinnedColumns<RowData> = Partial<
  Record<keyof RowData, 'left' | 'right'>
>;

export type PinnableProps<RowData> = {
  pinnedColumns: PinnedColumns<RowData>;
  onChange?: (pinnedColumns: PinnedColumns<RowData>) => void;
};

export type PaginationProps = {
  currentPage: number;
  pageSize: number;
};

type GroupingColumnAtKey<
  RowData,
  Key extends keyof RowData,
  RowId extends string = string,
> = Omit<ObjectColumn<RowData, Key, RowId>, 'cellRenderer'> & {
  expandedIds: RowId[];
  onRowExpansionToggle: (rowId: RowId, expanded: boolean) => void;
  /**
   * Called when the expanded IDs change in bulk, e.g. when the user clicks
   * "Expand All Row Groups" or "Close All Row Groups" from the header context menu.
   */
  onExpandedIdsChange?: (expandedIds: RowId[]) => void;
} & (
    | {
        /**
         * A component that renders the cell content, wrapped with AG Grid group chevrons etc.
         * Receives the row data as props (spread).
         *
         * Is not used if cellRenderer is provided.
         */
        InnerCellRenderer: ComponentType<NonNullable<RowData[Key]>>;
      }
    | {
        cellRenderer: ObjectColumn<RowData, Key, RowId>['cellRenderer'];
      }
  );

export type AnyObjectColumn<
  RowData,
  Key extends keyof RowData,
  RowId extends string = string,
> =
  | ObjectColumn<RowData, Key, RowId>
  | GroupingColumnAtKey<RowData, Key, RowId>;

export type GroupingColumn<RowData, RowId extends string = string> = {
  [Key in keyof RowData]: GroupingColumnAtKey<RowData, Key, RowId>;
}[keyof RowData];

export type GroupingColumnId<RowData> = {
  [Key in keyof RowData]: GroupingColumnAtKey<RowData, Key>;
}[keyof RowData]['id'] extends string
  ? string
  : never;

export type ColumnGrouping<RowData> = {
  groups: GroupingColumnId<RowData>[];
  onGroupingChange: (groups: GroupingColumnId<RowData>[]) => void;

  /**
   * Use a custom comparator for sorting the groups.
   *
   * This will be called to compare values on the same grouping level.
   *
   * @param a Value from the first row/group
   * @param b Value from the second row/group
   * @returns Negative number if a < b, positive if a > b, 0 if equal
   */
  comparator?: (a: string | undefined, b: string | undefined) => number;
};

export type ScrollPosition<RowId extends string = string> =
  VerticalScrollPosition<RowId> & HorizontalScrollPosition;

type VerticalScrollPosition<RowId extends string = string> =
  | {
      top: number;
    }
  | {
      rowId: RowId;
    };

type HorizontalScrollPosition = {
  left: number;
};

export type AbsoluteScrollPosition = {
  top: number;
  left: number;
};

/**
 * Props to control the scroll position of the grid.
 *
 * Supports two modes:
 * - SyncedScrollProps: Full control over the scroll position with sync callback.
 * - ScrollToRowProps: Scroll to a specific row by its ID.
 */
export type ScrollableProps<RowId extends string = string> =
  | SyncedScrollProps<RowId>
  | ScrollToRowProps<RowId>;

export type ScrollToRowScrollPosition<RowId extends string = string> = {
  rowId?: RowId;
};

export type ScrollToRowProps<RowId extends string = string> = {
  scrollPosition: ScrollToRowScrollPosition<RowId>;
};

/**
 * Lets you control the scroll position of the grid.
 *
 * Supports two modes:
 * - Absolute positioning: { top: number, left: number }
 * - Row-based scrolling: { rowId: string, left: number }
 *
 * When using rowId, AG Grid will scroll that row into view, then sync back
 * with the absolute position via onSync, converting the controlled state
 * to pixel values.
 *
 * Changes in AG Grid's scroll position (from user scrolling) are synced
 * via the onSync callback.
 */
type SyncedScrollProps<RowId extends string = string> = {
  scrollPosition: ScrollPosition<RowId>;
  /**
   * Called when AG Grid's scroll position changes.
   * You should update your state to keep the grid in sync.
   */
  onSync: (scrollPosition: AbsoluteScrollPosition) => void;
};

/**
 * Props to highlight specific rows in the grid.
 * Highlighted rows will have a visual indicator applied to all their cells.
 */
export type HighlightableProps<RowId extends string = string> = {
  /**
   * Array of row IDs to highlight.
   * All cells in these rows will be visually highlighted.
   */
  rowIds: RowId[];
};

export type UseControlledAGGridProps<
  RowData,
  FullWidthRowData = never,
  RowId extends string = string,
> = {
  rows: (Row<RowData, RowId> | FullWidthRow<FullWidthRowData>)[];
  columns: Column<RowData, RowId>[];
  reorderable?: ReorderableProps<RowData>;
  dragAndDroppable?: DragAndDroppableProps<RowData, RowId>;
  editable?: EditableProps<RowId>;
  selectable?: SelectableProps<RowId, RowData>;
  /**
   * For uncontrolled sorting, only provide a descriptor.
   * Use `null` to initialize the table without a selected sorting.
   */
  sortable?: SortableProps;
  /**
   * To enable pinning of columns, without specifying any columns, set pinnedColumns to an empty object.
   */
  pinnable?: PinnableProps<RowData>;
  pagination?: PaginationProps;
  columnGrouping?: ColumnGrouping<RowData>;
  processDataFromClipboard?: ProcessDataFromClipboard<RowData>;
  enableMovableColumns?: boolean;
  enableColumnChooser?: boolean;
  /**
   * Array of which columns should be visible and in which order.
   * If not provided, all columns are visible.
   * When provided, only the given columns will be shown, and they will be shown in the given order.
   */
  columnIds?: (keyof RowData)[];
  /**
   * Enables controlled scroll position for the grid.
   * Changes in AG Grid's scroll position are synced via the onSync callback.
   */
  scrollable?: ScrollableProps<RowId>;
  /**
   * Highlights specific rows in the grid.
   * All cells in the specified rows will have a visual highlight applied.
   */
  highlightable?: HighlightableProps<RowId>;
  /**
   * Simple renderer for full-width rows. Receives just the row's data (not AG Grid params)
   * and returns React elements.
   *
   * To use this:
   * 1. Add a `FullWidthRow<YourData>` to the rows array with `fullWidth: true`
   * 2. Implement this function to render that data
   *
   * Full-width rows automatically size to fit their content height.
   */
  fullWidthRowRenderer?: (data: FullWidthRowData) => ReactNode;
  /**
   * Sets a fixed height on the grid container.
   * When provided, the grid will be wrapped in a div with this height.
   */
  height?: string;
  /**
   * When true, the grid grows to fit all of its rows instead of scrolling
   * internally. Use together with `autoHeight` columns and omit `height`.
   */
  autoHeight?: boolean;
  /**
   * When true, shows a loading overlay over the grid body while keeping headers visible,
   * and suppresses the no-rows overlay.
   */
  loading?: boolean;
  /**
   * Renders a custom empty state when the table has no rows.
   * When provided, replaces the default EmptyState overlay.
   */
  renderEmptyState?: () => ReactNode;
  /**
   * Partial overrides for AG Grid's localeText (passed via gridOptions.localeText).
   * Use this to translate built-in AG Grid strings for the locale-aware consumer
   * (e.g. rowGroupColumnsEmptyMessage). Merged on top of the package defaults.
   */
  localeText?: Record<string, string>;
  /**
   * Returns the height for a row based on the row's data.
   * Use this when you can compute the height from data without measuring the DOM.
   */
  getRowHeight?: (rowData: RowData) => number | undefined;
  /**
   * Optional translated labels for the default empty state. Has no effect when
   * a custom `renderEmptyState` is provided.
   */
  emptyStateLabels?: Partial<TableEmptyStateLabels>;
  /**
   * Optional translated labels for the auto group column's chevron / drag
   * affordances and inline rename input. Threaded into the cell renderer
   * params and the cell editor params for the auto group column.
   */
  groupCellLabels?: Partial<GroupCellLabels>;
  /**
   * Optional translated labels for the row-selection column. Currently just the
   * visually hidden column header name.
   */
  selectionColumnLabels?: Partial<SelectionColumnLabels>;
  /**
   * Optional translated labels for AG Grid built-in strings rendered by the
   * package itself (currently the row-group drop zone placeholder). Merged
   * into `localeText` alongside any consumer override.
   */
  tableLocaleTextLabels?: Partial<TableLocaleTextLabels>;
  /**
   * AG Grid column auto-sizing strategy applied on first render. Use
   * `{ type: 'fitCellContents' }` to size each column to its widest cell, or
   * `{ type: 'fitGridWidth' }` to distribute the columns across the grid width.
   */
  autoSizeStrategy?:
    | SizeColumnsToContentStrategy
    | SizeColumnsToFitGridStrategy;
  /**
   * Hides the column header row entirely. Useful when column titles are
   * intentionally empty and the otherwise blank header would render as a
   * ghost row. When set, pair with `tableOperations.getDynamicTableHeight`
   * `{ excludeHeaderHeight: true }` so the computed height drops the header
   * band too.
   */
  headerHidden?: boolean;
} & AriaAttributes;
